import type { GTMCustomEvent, GTMEvent, GTMPurchaseParams } from '@skin-seoul/utils';
import CryptoJS from 'crypto-js';

/**
 * Free-form passthrough for add_to_cart dataLayer payloads.
 *
 * Master pre-migration pushed a flat shape `{event: 'add_to_cart',
 * 'x-fb-cd-content_type', 'x-fb-cd-content_name', 'x-fb-cd-content_ids',
 * value, currency, items: [...], event_name: 'add_to_cart', event_time,
 * item_variant, product_sku, ...}` via `logger.click('add_to_cart', ...)` →
 * `sendGTMEvent`. The caller builds this full shape (typically via
 * `createAddToCartParams` for PDP, or inline for quick-add cards) and passes
 * it through. We preserve that contract so downstream GTM variable mappings
 * (`{{dlv - items}}`, `{{dlv - x-fb-cd-content_ids}}`, etc.) continue to
 * resolve against the top-level fields.
 */
export type TrackAddToCartGtmExtras = Record<string, unknown>;

/**
 * Free-form passthrough for view_item dataLayer payloads.
 *
 * Same pattern as add_to_cart — master pushed `{event: 'page_view',
 * event_name: 'view_item', ...}` flat via `LoggingView`. Caller (PDP page)
 * builds the shape via `createPDPViewItemParams` and passes it through.
 */
export type TrackViewGtmExtras = Record<string, unknown>;
export type TrackBeginCheckoutGtmExtras = Record<string, unknown>;

export const mapAddToCart = (
  params: { productId: string; quantity?: number; variant?: string; uniqueId: string },
  extras?: TrackAddToCartGtmExtras,
): GTMCustomEvent => ({
  ...(extras ?? {}),
  event: 'add_to_cart',
  event_name: 'add_to_cart',
  transaction_id: buildActionTransactionId('add_to_cart', params.uniqueId),
});

export const mapViewItem = (
  params: { productId: string; uniqueId: string },
  extras?: TrackViewGtmExtras,
): GTMCustomEvent => ({
  ...(extras ?? {}),
  event: 'page_view',
  event_name: 'view_item',
  transaction_id: buildActionTransactionId('view_item', params.uniqueId),
});

export const mapBeginCheckout = (
  params: {
    sessionId: string;
    checkout: {
      currency: string;
      cartProducts: Array<{ id: string; quantity: number }>;
    };
  },
  extras?: TrackBeginCheckoutGtmExtras,
): GTMCustomEvent => ({
  ...(extras ?? {}),
  event: 'page_view',
  event_name: 'begin_checkout',
  transaction_id: buildCheckoutTransactionId(params),
});

export const mapPurchase = (params: GTMPurchaseParams & { orderId: string }): GTMEvent => ({
  event: 'purchase',
  ecommerce: params.ecommerce,
  user: params.user,
  platform: params.platform,
  // Master didn't emit `express_checkout` at all for normal checkout — only
  // express flow included it. Spread conditionally to avoid `undefined` keys
  // leaking into the dataLayer.
  ...(params.express_checkout ? { express_checkout: params.express_checkout } : {}),
});

const buildActionTransactionId = (eventName: 'add_to_cart' | 'view_item', uniqueId: unknown): string | null => {
  const normalizedUniqueId = normalizeRequiredString(uniqueId);
  return normalizedUniqueId ? sha256(`google:${eventName}:${normalizedUniqueId}`) : null;
};

const buildCheckoutTransactionId = ({ sessionId, checkout }: Parameters<typeof mapBeginCheckout>[0]): string | null => {
  const normalizedSessionId = normalizeRequiredString(sessionId);
  const currency = normalizeRequiredString(checkout.currency)?.toUpperCase();
  if (!normalizedSessionId || !currency || !/^[A-Z]{3}$/.test(currency) || !Array.isArray(checkout.cartProducts)) {
    return null;
  }

  const quantitiesByProductId = new Map<string, number>();
  for (const item of checkout.cartProducts) {
    const productId = normalizeRequiredString(item?.id);
    const quantity = item?.quantity;
    if (!productId || !Number.isSafeInteger(quantity) || quantity <= 0) {
      return null;
    }
    const totalQuantity = (quantitiesByProductId.get(productId) ?? 0) + quantity;
    if (!Number.isSafeInteger(totalQuantity)) {
      return null;
    }
    quantitiesByProductId.set(productId, totalQuantity);
  }
  if (quantitiesByProductId.size === 0) {
    return null;
  }

  const items = Array.from(quantitiesByProductId).sort(([left], [right]) => {
    if (left < right) {
      return -1;
    }
    if (left > right) {
      return 1;
    }
    return 0;
  });
  return sha256(JSON.stringify(['google', 'begin_checkout', 'v1', normalizedSessionId, currency, items]));
};

const normalizeRequiredString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  return value.trim() || undefined;
};

const sha256 = (value: string): string => CryptoJS.SHA256(value).toString(CryptoJS.enc.Hex);
